import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { API_ORIGIN } from '../api/client';
import {
  usePostToSlack,
  useSendSlackDms,
  useSlack,
  useSlackChannels,
  useSlackDraft,
} from '../api/queries';
import { Button, Checkbox, Empty, MenuButton, Modal, Pill, SkeletonRows, useNotices } from '../ui';
import { since } from '../lib/time';

/**
 * Posting what a meeting produced to a channel (PRD 33).
 *
 * ## Nothing goes unreviewed, and not everything goes
 *
 * Vladimir, 2026-09-21: *"I need to be able to select what commitments are
 * added to the message to slack and which are not, sometimes I don't want to
 * send them all."* So every line has a box and the button says how many are
 * ticked.
 *
 * ## One message per channel, and the page says which
 *
 * And then: *"i cant post in several channels, as after one its just updates
 * previous message"* — the first cut kept one message per MEETING. A meeting
 * can be in as many channels as somebody has posted it to, each with its own
 * selection, because the room that needs the decisions is not always the room
 * that needs the promises.
 *
 * So choosing a channel changes what is ticked: a channel that already has the
 * message starts from **what that message says**, so pressing update edits it
 * rather than rewriting it from scratch; a channel with none starts from
 * everything, because the common case is all of it.
 */
export function SlackPost({
  meetingId,
  onClose,
}: {
  meetingId: string;
  onClose: () => void;
}): ReactElement {
  const slack = useSlack();
  const [where, setWhere] = useState<'channel' | 'owners'>('channel');

  if (slack.isPending)
    return (
      <Modal title="Post to Slack" onClose={onClose}>
        <SkeletonRows rows={3} />
      </Modal>
    );

  if (!slack.data?.connected)
    return (
      <Modal title="Post to Slack" onClose={onClose}>
        <Empty lead="Slack is not connected yet.">
          {slack.data?.canManage ? (
            <>
              An administrator connects it once for the whole workspace, in{' '}
              <Link to="/settings/connected">Connected apps</Link>.
            </>
          ) : (
            'An administrator connects it once for the whole workspace.'
          )}
        </Empty>
      </Modal>
    );

  const tab = (key: 'channel' | 'owners', label: string) => (
    <button
      type="button"
      role="tab"
      aria-selected={where === key}
      className={`tab ${where === key ? 'on' : ''}`}
      onClick={() => setWhere(key)}
      style={{
        background: 'none',
        border: 0,
        borderBottom: where === key ? '2px solid var(--green)' : '2px solid transparent',
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );

  return (
    <Modal title="Post to Slack" onClose={onClose}>
      <div className="stack" style={{ gap: 14 }}>
        {/* ⚠ Two answers to "who should see this", side by side: a room, or
            each person who took something on (PRD 56). */}
        <div className="subtabs" role="tablist" aria-label="Where it goes">
          {tab('channel', 'A channel')}
          {tab('owners', 'Each owner, privately')}
        </div>
        {where === 'channel' ? (
          <ToChannel meetingId={meetingId} onClose={onClose} />
        ) : (
          <ToOwners meetingId={meetingId} onClose={onClose} canManage={slack.data.canManage} />
        )}
      </div>
    </Modal>
  );
}

/** One message in one channel, with what the room should see ticked (PRD 33). */
function ToChannel({
  meetingId,
  onClose,
}: {
  meetingId: string;
  onClose: () => void;
}): ReactElement {
  const draft = useSlackDraft(meetingId, true);
  const channels = useSlackChannels(true);
  const post = usePostToSlack(meetingId);
  const { show } = useNotices();

  const [channelId, setChannelId] = useState<string | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());

  const d = draft.data;
  const already = d?.posted.find((p) => p.channelId === channelId) ?? null;

  // The first channel to offer: the series' own, else the one it is already
  // in, else nothing — never a guess that posts somewhere unintended.
  useEffect(() => {
    if (!d || channelId !== null) return;
    setChannelId(d.suggestedChannelId ?? d.posted[0]?.channelId ?? '');
  }, [d, channelId]);

  // ⚠ What is ticked follows the channel, because it is that channel's
  // message being written: what it already says, or everything for a new one.
  useEffect(() => {
    if (!d || channelId === null) return;
    const inThis = d.posted.find((p) => p.channelId === channelId);
    setPicked(
      new Set(
        inThis
          ? [
              ...d.commitments.filter((c) => c.postedIn.includes(channelId)).map((c) => c.id),
              ...d.decisions.filter((x) => x.postedIn.includes(channelId)).map((x) => x.id),
            ]
          : [...d.commitments.map((c) => c.id), ...d.decisions.map((x) => x.id)],
      ),
    );
  }, [d, channelId]);

  const toggle = (id: string, on: boolean) =>
    setPicked((was) => {
      const next = new Set(was);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const count = picked.size;
  const untaggable = (d?.commitments ?? []).filter((c) => picked.has(c.id) && !c.taggable);
  const rows = channels.data ?? [];
  const chosen = rows.find((c) => c.id === channelId);
  const label = chosen
    ? `${chosen.isPrivate ? '🔒' : '#'} ${chosen.name}`
    : already?.channelName
      ? `# ${already.channelName}`
      : 'Choose a channel…';

  const send = () => {
    if (!d || !channelId || count === 0) return;
    post.mutate(
      {
        channelId,
        ...(chosen ? { channelName: chosen.name } : {}),
        commitmentIds: d.commitments.filter((c) => picked.has(c.id)).map((c) => c.id),
        decisionIds: d.decisions.filter((x) => picked.has(x.id)).map((x) => x.id),
      },
      {
        onSuccess: (result) => {
          onClose();
          show({
            tone: 'success',
            text:
              `${result.commitments} ${result.updated ? 'now in' : 'posted to'} ` +
              `${chosen ? `#${chosen.name}` : 'the channel'}` +
              (result.decisions ? ` with ${result.decisions} decided` : '') +
              (result.untagged.length ? ` · ${result.untagged.join(', ')} named, not tagged` : ''),
          });
        },
        onError: (err) => show({ tone: 'failure', text: err.message }),
      },
    );
  };

  return (
    <>
      {draft.isPending || !d ? (
        <SkeletonRows rows={4} />
      ) : (
        <div className="stack" style={{ gap: 14 }}>
          {/* ⚠ A menu, not a select: it filters as soon as there are more than
              a handful, which is every real workspace. */}
          <div className="cluster">
            <MenuButton
              label={label}
              items={rows.map((c) => ({
                label: `${c.isPrivate ? '🔒' : '#'} ${c.name}`,
                sub: d.posted.some((p) => p.channelId === c.id)
                  ? 'already has this meeting'
                  : undefined,
                current: c.id === channelId,
                onSelect: () => setChannelId(c.id),
              }))}
            />
            {channels.isPending ? <span className="why">reading your channels…</span> : null}
            {d.suggestedChannelName ? (
              <span className="why">this series posts to #{d.suggestedChannelName}</span>
            ) : null}
          </div>
          {/* Aleksandr, 2026-10-08: when the app has to be added, and when not —
              "я запутался". Said where the list is missing something. */}
          <span className="why">
            a private channel shows once Ptitim is in it — type /invite @Ptitim there
          </span>

          {/* Where this meeting already is — so posting a second time
              somewhere else is a deliberate act rather than a surprise. */}
          {d.posted.length ? (
            <div className="cluster" style={{ gap: 6 }}>
              <span className="why">already in</span>
              {d.posted.map((p) => (
                <Pill key={p.channelId} tone={p.channelId === channelId ? 'green' : undefined}>
                  #{p.channelName ?? p.channelId} · {p.commitments + p.decisions} · {since(p.at)}
                </Pill>
              ))}
            </div>
          ) : null}

          {already ? (
            <span className="t-secondary">
              #{already.channelName ?? already.channelId} already has this meeting. Posting edits
              that message; anything you untick leaves it, and every other channel stays as it is.
            </span>
          ) : null}

          {d.commitments.length === 0 && d.decisions.length === 0 ? (
            <Empty lead="Nothing to post.">
              This meeting produced no commitments and no decisions.
            </Empty>
          ) : (
            <div className="stack" style={{ gap: 10 }}>
              {d.commitments.length ? (
                <div className="stack" style={{ gap: 8 }}>
                  <span className="eyebrow">What people said they would do</span>
                  {d.commitments.map((c) => (
                    <div key={c.id} className="stack" style={{ gap: 2 }}>
                      <Checkbox
                        label={c.text}
                        checked={picked.has(c.id)}
                        onChange={(e) => toggle(c.id, e.target.checked)}
                      />
                      <span className="why" style={{ marginLeft: 26 }}>
                        {c.ownerName ?? 'nobody yet'}
                        {c.taggable ? ' · tagged' : ' · named, not tagged'}
                        {c.postedIn.length ? ` · in ${c.postedIn.length} channel(s)` : ''}
                      </span>
                    </div>
                  ))}
                </div>
              ) : null}

              {d.decisions.length ? (
                <div className="stack" style={{ gap: 8 }}>
                  <span className="eyebrow">Decided</span>
                  {d.decisions.map((x) => (
                    <Checkbox
                      key={x.id}
                      label={x.text}
                      checked={picked.has(x.id)}
                      onChange={(e) => toggle(x.id, e.target.checked)}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          )}

          {untaggable.length ? (
            <span className="t-secondary">
              Slack has no account for {untaggable.map((c) => c.ownerName).join(', ')} — they are
              named in the message rather than tagged.
            </span>
          ) : null}

          <div className="cluster modal-actions">
            <Button
              variant="primary"
              onClick={send}
              disabled={!channelId || count === 0}
              working={post.isPending ? 'Posting…' : false}
            >
              {already ? `Update ${label}` : `Post ${count} to ${label}`}
            </Button>
            <Button variant="quiet" onClick={onClose}>
              Not now
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Each owner, privately (PRD 56): their own chosen actions, and the sender's
 * beside them.
 *
 * Vladimir, 2026-09-23: *"I select two actions by two owner and it send only
 * to these two owners dm messages. BUT if I select actions where I am the
 * owner they shall be sent to each user as well, so they can track how I am
 * progressing as well. For example after 1/1 I will send to the person all the
 * actions - my and his."* So the page says, before anything is sent, exactly
 * who gets what — and who will not, because Slack does not know them.
 */
function ToOwners({
  meetingId,
  onClose,
  canManage,
}: {
  meetingId: string;
  onClose: () => void;
  canManage: boolean;
}): ReactElement {
  const draft = useSlackDraft(meetingId, true);
  const send = useSendSlackDms(meetingId);
  const { show } = useNotices();
  const [picked, setPicked] = useState<Set<string> | null>(null);
  // ⚠ Both kept (Vladimir, 2026-09-23: "leave both options"). From me: my own
  // DM with each person, which needs my own Slack connected. From the bot:
  // each person's conversation with Ptitim, which the workspace grants.
  const [from, setFrom] = useState<'me' | 'bot' | null>(null);
  const d = draft.data;

  // Everything, to start: after a 1:1 the common case is all of it.
  useEffect(() => {
    if (d && picked === null)
      setPicked(new Set([...d.commitments.map((c) => c.id), ...d.decisions.map((x) => x.id)]));
  }, [d, picked]);
  // From me when I can, since that is where people talk to each other.
  useEffect(() => {
    if (d && from === null) setFrom(d.meReady || !d.dmReady ? 'me' : 'bot');
  }, [d, from]);

  if (draft.isPending || !d || !picked || !from) return <SkeletonRows rows={4} />;
  if (d.commitments.length === 0)
    return <Empty lead="Nothing to send.">This meeting produced no actions.</Empty>;

  const ready = from === 'me' ? d.meReady : d.dmReady;
  const choice = (key: 'me' | 'bot', label: string) => (
    <Button size="sm" variant={from === key ? 'primary' : 'quiet'} onClick={() => setFrom(key)}>
      {label}
    </Button>
  );

  const toggle = (id: string, on: boolean) =>
    setPicked((was) => {
      const next = new Set(was ?? []);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  const isMine = (c: { ownerEmail: string | null }) =>
    !!c.ownerEmail && !!d.me && c.ownerEmail.toLowerCase() === d.me;
  const chosen = d.commitments.filter((c) => picked.has(c.id));
  const mine = chosen.filter(isMine);
  const owners = new Map<string, { name: string; count: number; reachable: boolean }>();
  for (const c of chosen) {
    if (!c.ownerEmail || isMine(c)) continue;
    const key = c.ownerEmail.toLowerCase();
    const was = owners.get(key);
    owners.set(key, {
      name: c.ownerName ?? c.ownerEmail,
      count: (was?.count ?? 0) + 1,
      reachable: c.taggable,
    });
  }
  const reach = [...owners.values()].filter((o) => o.reachable);
  const cannot = [...owners.values()].filter((o) => !o.reachable);
  const unowned = chosen.filter((c) => !c.ownerEmail).length;
  const decided = d.decisions.filter((x) => picked.has(x.id)).length;
  const names = (list: Array<{ name: string }>) => list.map((o) => o.name).join(', ');

  const go = () =>
    send.mutate(
      {
        commitmentIds: chosen.map((c) => c.id),
        decisionIds: d.decisions.filter((x) => picked.has(x.id)).map((x) => x.id),
        as: from,
      },
      {
        onSuccess: (result) => {
          onClose();
          show({
            tone: 'success',
            text:
              `Sent to ${result.sent.map((x) => x.name).join(', ') || 'nobody'}` +
              (result.mine ? ` — each with your ${result.mine} alongside` : '') +
              (result.unreachable.length
                ? ` · not in Slack: ${result.unreachable.join(', ')}`
                : ''),
          });
        },
        onError: (err) => show({ tone: 'failure', text: err.message }),
      },
    );

  return (
    <div className="stack" style={{ gap: 14 }}>
      <div className="cluster" style={{ gap: 6 }}>
        <span className="why">send it</span>
        {choice('me', 'From me, in our DM')}
        {choice('bot', 'From the Ptitim bot')}
      </div>
      <span className="t-secondary">
        {from === 'me'
          ? 'Each owner gets a message from you, in your own DM with them, with their actions — and yours beside them so they can follow along.'
          : 'Each owner gets a direct message from the Ptitim bot with their own actions, and yours beside them so they can follow along.'}{' '}
        Nobody gets anybody else&apos;s.
      </span>
      {/* ⚠ Said here, in the place somebody is about to press Send — not
          discovered as a refusal afterwards. */}
      {!ready ? (
        from === 'me' ? (
          <Empty lead="Connect your own Slack first.">
            So the messages can come from you: <a href={slackMineUrl}>Connect my Slack</a>. It asks
            for sending as you and opening a DM — nothing that reads.
          </Empty>
        ) : (
          <Empty lead="Slack needs one more permission for the bot to send these.">
            {canManage ? (
              <>
                Reconnect it in <Link to="/settings/connected">Connected apps</Link> and allow
                direct messages — or send them from you instead.
              </>
            ) : (
              'An administrator allows it by reconnecting Slack in Connected apps — or send them from you instead.'
            )}
          </Empty>
        )
      ) : null}
      <div className="stack" style={{ gap: 8 }}>
        {d.commitments.map((c) => (
          <div key={c.id} className="stack" style={{ gap: 2 }}>
            <Checkbox
              label={c.text}
              checked={picked.has(c.id)}
              onChange={(e) => toggle(c.id, e.target.checked)}
            />
            <span className="why" style={{ marginLeft: 26 }}>
              {isMine(c) ? 'yours — goes to everyone below' : (c.ownerName ?? 'nobody yet')}
              {!isMine(c) && c.ownerEmail && !c.taggable ? ' · not in this Slack' : ''}
              {c.sentTo.length ? ` · already sent to ${c.sentTo.length}` : ''}
            </span>
          </div>
        ))}
      </div>

      {/* ⚠ The meeting's, not one owner's — so every recipient gets the same
          ones (Vladimir, 2026-09-23: "share whats decided to the DMs as
          well"). */}
      {d.decisions.length ? (
        <div className="stack" style={{ gap: 8 }}>
          <span className="eyebrow">Decided — goes to everyone below</span>
          {d.decisions.map((x) => (
            <Checkbox
              key={x.id}
              label={x.text}
              checked={picked.has(x.id)}
              onChange={(e) => toggle(x.id, e.target.checked)}
            />
          ))}
        </div>
      ) : null}

      <div className="stack" style={{ gap: 4 }}>
        <span className="eyebrow">Who gets what</span>
        {reach.length ? (
          reach.map((o) => (
            <span key={o.name} className="t-ui">
              {o.name} — {o.count} of theirs
              {mine.length ? ` + ${mine.length} of yours` : ''}
              {decided ? ` + ${decided} decided` : ''}
            </span>
          ))
        ) : (
          <span className="t-secondary">
            Pick at least one action somebody else owns — yours go to them alongside their own.
          </span>
        )}
        {cannot.length ? (
          <span className="t-secondary">
            {names(cannot)} {cannot.length === 1 ? 'is' : 'are'} not in this Slack, so theirs are
            not sent.
          </span>
        ) : null}
        {unowned ? (
          <span className="t-secondary">
            {unowned} with no owner {unowned === 1 ? 'goes' : 'go'} to nobody.
          </span>
        ) : null}
      </div>

      {d.dms.length ? (
        <div className="cluster" style={{ gap: 6 }}>
          <span className="why">already sent to</span>
          {d.dms.map((x) => (
            <Pill key={x.channelId}>
              {x.recipientName ?? 'someone'} · {x.asUser ? 'from you' : 'from Ptitim'} ·{' '}
              {x.commitments} · {since(x.at)}
            </Pill>
          ))}
        </div>
      ) : null}

      <div className="cluster modal-actions">
        <Button
          variant="primary"
          onClick={go}
          disabled={reach.length === 0 || !ready}
          working={send.isPending ? 'Sending…' : false}
        >
          {reach.length === 1 ? `Send to ${reach[0].name}` : `Send to ${reach.length} people`}
        </Button>
        <Button variant="quiet" onClick={onClose}>
          Not now
        </Button>
      </div>
    </div>
  );
}

/**
 * Where the workspace sends an administrator to connect Slack.
 *
 * ⚠ Under `/v1`, because the session cookie is — the door has to know who is
 * pressing it, and an unversioned one never receives the cookie: the first cut
 * bounced every press to the sign-in page and on to Today, for somebody who
 * was signed in the whole time. Only Slack's own two URLs are unversioned.
 */
export const slackInstallUrl = `${API_ORIGIN}/v1/slack/start`;

/**
 * Where a person connects their OWN Slack, so a message can go out as them
 * (PRD 56 §6). Under `/v1` for the same reason as the install door.
 */
export const slackMineUrl = `${API_ORIGIN}/v1/slack/me/start`;
